/**
 * Small shared parts of the technician screens (FM Technician rebuild, R1).
 *
 * The rule they exist for: EVERY STATUS COLOUR CARRIES A WORD AND AN ICON
 * (D24, DESIGN-CHECK §1 "Status pills"). A colour alone is unreadable to a
 * colour-blind technician and in sunlight; `Pill` takes a tone, a word and an
 * icon, and will not draw one without the others.
 */
import type React from 'react';
import {
  CheckCircleIcon, ClockIcon, ExclamationTriangleIcon, InformationCircleIcon, NoSymbolIcon, XCircleIcon,
} from '@heroicons/react/24/solid';
import { routeRequestError } from '@/utils/validation';

export type Tone = 'red' | 'amber' | 'green' | 'blue' | 'grey';

const TONE: Record<Tone, string> = {
  red: 'bg-danger-50 text-danger-700 border-danger-200',
  amber: 'bg-warning-50 text-warning-800 border-warning-200',
  green: 'bg-success-50 text-success-700 border-success-200',
  blue: 'bg-info-50 text-info-700 border-info-200',
  grey: 'bg-surface-muted text-content-secondary border-line',
};

const ICON: Record<Tone, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  red: XCircleIcon,
  amber: ExclamationTriangleIcon,
  green: CheckCircleIcon,
  blue: InformationCircleIcon,
  grey: NoSymbolIcon,
};

/** A status pill: tone + word + icon, 14 px. */
export function Pill({ tone, word, icon, testid, wrap = false }: {
  tone: Tone; word: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>; testid?: string;
  /** A long pill that names a person ("Signed by …", "Seen by …") wraps
   *  inside the screen at 360 px rather than running off it; the icon stays. */
  wrap?: boolean;
}) {
  const Icon = icon ?? ICON[tone];
  const shape = wrap
    ? 'max-w-full items-start whitespace-normal break-words rounded-2xl'
    : 'items-center whitespace-nowrap rounded-full';
  return (
    <span data-testid={testid} data-tone={tone}
          className={`inline-flex gap-1 border px-2.5 py-0.5 text-sm font-medium ${shape} ${TONE[tone]}`}>
      <Icon className={`h-4 w-4 shrink-0${wrap ? ' mt-0.5' : ''}`} aria-hidden="true" />
      {wrap ? <span className="min-w-0">{word}</span> : word}
    </span>
  );
}

export { ClockIcon };

/** The server's words for a refusal (written for people), else `fallback`. */
export function serverWords(e: unknown, fallback: string): string {
  return routeRequestError(e, {})?.message ?? fallback;
}

/** HTTP status of an axios error, or 0. */
export function statusOf(e: unknown): number {
  return (e as { response?: { status?: number } })?.response?.status ?? 0;
}

/** "Expires in 1 h 05 min" / "Expired 12 min ago" — whole minutes. */
export function countdown(to: Date, now: Date = new Date()): { expired: boolean; text: string } {
  const mins = Math.round((to.getTime() - now.getTime()) / 60_000);
  const abs = Math.abs(mins);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  const span = h > 0 ? `${h} h ${String(m).padStart(2, '0')} min` : `${m} min`;
  return mins <= 0 ? { expired: true, text: `Expired ${span} ago` } : { expired: false, text: `Expires in ${span}` };
}
